import { useEffect, useMemo, useRef, useState } from "react"
import { conceptAtlasItemsFor } from "./RepositoryConcepts"

const onboardingConceptFetchDebounceMs = 300
const onboardingConceptFetchRetryMs = 10_000

// Reveal ramps with step progress so the galaxy is faintly present from the
// first step and fully formed behind the completion screen.
export function onboardingSkyReveal(snapshot, finished = false) {
  if (finished) return 1
  const totalCount = Number(snapshot?.totalCount ?? snapshot?.total_count)
  if (!Number.isFinite(totalCount) || totalCount <= 0) return 0.25
  const completedCount = Math.max(0, Number(snapshot?.completedCount ?? snapshot?.completed_count) || 0)
  const skippedCount = Math.max(0, Number(snapshot?.skippedCount ?? snapshot?.skipped_count) || 0)
  const runningCount = Math.max(0, Number(snapshot?.runningCount ?? snapshot?.running_count) || 0)
  const progress = Math.min(1, (completedCount + skippedCount + 0.5 * runningCount) / totalCount)
  return Math.round((0.25 + 0.75 * progress) * 100) / 100
}

// The 1s status poll already carries concept readiness counts; a change in
// this key is the only trigger for refetching the forming atlas.
export function onboardingConceptFetchKey(snapshot, finished = false) {
  if (!snapshot?.conceptsPath) return null
  const tasks = Array.isArray(snapshot.tasks) ? snapshot.tasks : []
  const task = tasks.find((entry) => entry?.key === "generating_concepts")
  if (!task) {
    const syncKey = syncConceptFetchKey(tasks, finished)
    if (syncKey) return syncKey
  }
  const counts = task?.counts || {}
  const selectionReady = Boolean(counts.conceptSelectionReady ?? counts.concept_selection_ready)
  if (!finished && !selectionReady) return null
  return [
    Number(counts.selectedConceptCount ?? counts.selected_concept_count) || 0,
    Number(counts.readyStoryCount ?? counts.ready_story_count) || 0,
    Number(counts.readyConstellationCount ?? counts.ready_constellation_count) || 0,
    finished ? "done" : "live"
  ].join("/")
}

// Sync-now runs have no generating_concepts step with readiness counts; their
// atlas already exists and is swapped by the syncing_concepts step, so the
// fetch key tracks that step's status — loading the current atlas up front and
// refetching once the swap lands.
function syncConceptFetchKey(tasks, finished) {
  const task = tasks.find((entry) => entry?.key === "syncing_concepts")
  if (!task) return null

  return [ "sync", task.status || "queued", finished ? "done" : "live" ].join("/")
}

// Slim atlas bodies carrying only the fields the starfield reads. Code volume
// is pre-summed into one synthetic contribution day so relative star sizes
// match the real atlas without hauling full commit histories through state.
export function onboardingSkyBodies(items, domainEnd = null) {
  if (!Array.isArray(items) || items.length === 0) return []

  return conceptAtlasItemsFor(items, domainEnd ? { end: domainEnd } : null).map((item) => ({
    key: item.key,
    atlasType: item.atlasType,
    title: item.title,
    color: item.color,
    glowColor: item.glowColor,
    commitCount: item.commitCount,
    luminance: item.luminance,
    contributionDays: [{ codeVolume: onboardingSkyCodeVolume(item) }],
    facets: (Array.isArray(item.facets) ? item.facets : []).map((facet) => ({
      key: facet.key,
      label: facet.label,
      parentKey: facet.parentKey,
      color: facet.color
    })),
    relationships: (Array.isArray(item.relationships) ? item.relationships : []).map((relationship) => ({
      targetKey: relationship.targetKey,
      kind: relationship.kind,
      direction: relationship.direction,
      strength: relationship.strength
    }))
  }))
}

function onboardingSkyCodeVolume(item) {
  const days = Array.isArray(item?.contributionDays) ? item.contributionDays : []
  const dayVolume = days.reduce((total, day) => total + Math.max(0, Number(day?.codeVolume) || 0), 0)
  if (dayVolume > 0) return dayVolume

  const commits = Array.isArray(item?.commits) ? item.commits : []
  return commits.reduce((total, commit) => (
    total + Math.max(0, Number(commit?.additions) || 0) + Math.max(0, Number(commit?.deletions) || 0)
  ), 0)
}

export function RepositoryOnboardingSky({ snapshot, finished = false, initialConceptItems = null }) {
  const [items, setItems] = useState(() => (Array.isArray(initialConceptItems) ? initialConceptItems : null))
  const [sceneEpoch, setSceneEpoch] = useState(0)
  const [documentVisible, setDocumentVisible] = useState(() => typeof document === "undefined" || !document.hidden)
  // Mount-time preference read, same posture as the scene's own idle check:
  // reduced motion keeps the static gradient sky and never mounts WebGL.
  const [reducedMotion] = useState(() => typeof window !== "undefined" && Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches))
  const [domainEnd] = useState(() => new Date().toISOString().slice(0, 10))
  const stageRef = useRef(null)
  const sceneRef = useRef(null)
  const inFlightRef = useRef(null)
  const fetchedKeyRef = useRef(null)
  const snapshotIdRef = useRef(snapshot?.id ?? null)
  const conceptsPath = snapshot?.conceptsPath || null
  const fetchKey = onboardingConceptFetchKey(snapshot, finished)
  const bodies = useMemo(() => onboardingSkyBodies(items, domainEnd), [domainEnd, items])
  const signature = useMemo(() => JSON.stringify(bodies), [bodies])

  useEffect(() => {
    function handleVisibilityChange() {
      setDocumentVisible(!document.hidden)
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [])

  // A re-run of onboarding is a new sync event: drop the previous run's stars.
  useEffect(() => {
    const snapshotId = snapshot?.id ?? null
    if (snapshotIdRef.current === snapshotId) return
    snapshotIdRef.current = snapshotId
    fetchedKeyRef.current = null
    setItems(null)
  }, [snapshot?.id])

  useEffect(() => {
    if (!conceptsPath || !fetchKey || !documentVisible) return undefined
    if (fetchKey === fetchedKeyRef.current) return undefined

    let retryTimer = null
    // Coalesce readiness-count bursts arriving from the 1s status poll.
    const debounceTimer = window.setTimeout(load, onboardingConceptFetchDebounceMs)

    function load() {
      inFlightRef.current?.abort()
      const controller = new AbortController()
      inFlightRef.current = controller
      const separator = conceptsPath.includes("?") ? "&" : "?"

      fetch(`${conceptsPath}${separator}limit=1`, {
        cache: "no-store",
        headers: { Accept: "application/json" },
        signal: controller.signal
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Concept preview request failed: ${response.status}`)
          return response.json()
        })
        .then((payload) => {
          fetchedKeyRef.current = fetchKey
          setItems(payload?.concepts?.items || [])
        })
        .catch((error) => {
          if (error?.name === "AbortError") return
          retryTimer = window.setTimeout(load, onboardingConceptFetchRetryMs)
        })
    }

    return () => {
      window.clearTimeout(debounceTimer)
      if (retryTimer) window.clearTimeout(retryTimer)
      inFlightRef.current?.abort()
      inFlightRef.current = null
    }
  }, [conceptsPath, documentVisible, fetchKey])

  useEffect(() => {
    if (reducedMotion) return undefined
    const stage = stageRef.current
    if (!stage) return undefined

    let cancelled = false
    // Carry the outgoing framing so accreting stars never visibly move the sky.
    const previousCamera = sceneRef.current?.cameraState?.() || null
    sceneRef.current?.dispose()
    sceneRef.current = null

    import("./ConceptStarfieldScene")
      .then(({ mountConceptStarfieldScene }) => {
        if (cancelled) return
        // No interaction callbacks: the sky is inert scenery behind a
        // pointer-events-none layer; the onboarding UI keeps every hit target.
        sceneRef.current = mountConceptStarfieldScene(stage, bodies, {
          initialCamera: previousCamera,
          onContextLost: () => setSceneEpoch((current) => current + 1)
        })
      })
      .catch(() => {})

    return () => {
      cancelled = true
      sceneRef.current?.dispose()
      sceneRef.current = null
    }
  }, [signature, sceneEpoch, reducedMotion]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="repository-onboarding-sky"
      aria-hidden="true"
      style={{ "--onboarding-sky-reveal": onboardingSkyReveal(snapshot, finished) }}
    >
      <div className="repository-onboarding-sky-stage" ref={stageRef} />
    </div>
  )
}
